"use client";

import React, { useState } from "react";
import { Building2, Truck, MapPin, Phone, Mail, Clock, CircleCheck, ArrowRight, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "@/components/common/Link";
const SHARED_DATA = {
  address: "842 Industrial Way, Olive Hill, KY 41164",
  phone: "(606) 286-4532",
  email: "service@olivehillequipment.com",
  hours: "Mon-Fri: 7am-4pm EST",
  googleMapsUrl: "https://maps.google.com/?q=842+Industrial+Way,+Olive+Hill,+KY+41164"
};
export default function InquiryGateway() {
  const [userType, setUserType] = useState<"manufacturer" | "distributor">("manufacturer");
  return <section data-section-id="7271" id="submit-a-repair-ticket" className="relative w-full py-24 overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: "linear-gradient(var(--color-primary) 1px, transparent 1px), linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} />
      
      <div className="container relative mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          
          {}
          <div className="lg:col-span-5 flex flex-col justify-between h-full space-y-12">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 text-primary text-[10px] font-bold tracking-widest uppercase font-mono">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Accepting Repairs
              </div>
              
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-foreground uppercase">
                HEAVY-DUTY REPAIR. <br />
                <span className="text-primary">PRECISION RESULTS.</span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                Minimize downtime with expert Miller, Lincoln, and Hypertherm service. Our factory-trained technicians specialize in complex circuit board diagnostics and mechanical restoration.
              </p>
            </div>

            <div className="space-y-8">
              <div className="grid gap-6">
                <div className="flex items-start gap-4 group">
                  <div className="mt-1 p-2 rounded-sm bg-secondary/20 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground uppercase text-sm tracking-tight">Main Facility</h4>
                    <p className="text-muted-foreground mt-1 text-sm">1940 Semora Rd, Roxboro, NC 27574</p>
                    <Link to={SHARED_DATA.googleMapsUrl} newTab className="text-xs text-primary font-bold mt-2 inline-flex items-center hover:underline uppercase tracking-wider">
                      Get Directions <ArrowRight className="ml-1 size-3" />
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="mt-1 p-2 rounded-sm bg-secondary/20 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground uppercase text-sm tracking-tight">Technical Support</h4>
                    <p className="text-muted-foreground mt-1 text-sm">(336) 828 - 6177</p>
                    <p className="text-[10px] text-muted-foreground mt-1 font-mono">{SHARED_DATA.hours}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="mt-1 p-2 rounded-sm bg-secondary/20 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground uppercase text-sm tracking-tight">Service Inquiries</h4>
                    <p className="text-muted-foreground mt-1 text-sm">repairs@ohequipservice.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="relative bg-card/60 backdrop-blur-md border border-border/50 p-8 md:p-10 rounded-sm shadow-2xl shadow-black/40">
              {}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-primary/10 to-transparent rounded-tr-sm pointer-events-none" />
              
              <div className="mb-8 space-y-4">
                <h3 className="text-2xl font-black text-foreground uppercase tracking-tight">Submit Repair Ticket</h3>
                <p className="text-muted-foreground text-xs font-medium">Select equipment type to prioritize your request.</p>
                
                <ToggleGroup type="single" value={userType} onValueChange={value => value && setUserType(value as "manufacturer" | "distributor")} className="justify-start w-full p-1 bg-muted/30 rounded-sm border border-border/50">
                  <ToggleGroupItem value="manufacturer" className="flex-1 gap-2 rounded-sm text-xs font-bold uppercase tracking-wider data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                    <Building2 className="size-4" />
                    Welder Repair
                  </ToggleGroupItem>
                  <ToggleGroupItem value="distributor" className="flex-1 gap-2 rounded-sm text-xs font-bold uppercase tracking-wider data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                    <Truck className="size-4" />
                    Plasma Repair
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              <FormProvider formKey="repair_ticket_form_request_service" formId="repair_ticket_form_request_service" sectionName="inquiry-gateway" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Submit Repair Ticket" successMessage="Thank you. Your repair ticket has been logged and assigned to a technician.">
                <InquiryFormContent userType={userType} />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function InquiryFormContent({
  userType
}: {
  userType: "manufacturer" | "distributor";
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-2 border border-primary/20">
          <CircleCheck className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-black text-foreground uppercase tracking-tight">Ticket Logged</h3>
        <p className="text-muted-foreground max-w-xs mx-auto text-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-sm border-primary/20 hover:bg-primary/10 hover:text-primary font-bold uppercase tracking-widest text-xs">
          Log Another Ticket
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold font-mono">Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="border-0 border-b-2 border-input rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors h-10 placeholder:text-muted-foreground/30 font-medium" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold font-mono">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="maintenance@shop.com" {...field} className="border-0 border-b-2 border-input rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors h-10 placeholder:text-muted-foreground/30 font-medium" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold font-mono">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(606) 555-0123" {...field} className="border-0 border-b-2 border-input rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors h-10 placeholder:text-muted-foreground/30 font-medium" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold font-mono">
                {userType === 'manufacturer' ? 'Welder Brand' : 'Plasma Brand'}
              </FormLabel>
              <FormControl>
                <Input placeholder={userType === 'manufacturer' ? 'e.g., Miller Electric' : 'e.g., Hypertherm'} {...field} className="border-0 border-b-2 border-input rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors h-10 placeholder:text-muted-foreground/30 font-medium" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem className="space-y-1 pt-2">
            <FormLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold font-mono">Model & Serial Number</FormLabel>
            <FormControl>
              <Input placeholder={userType === 'manufacturer' ? 'e.g., Trailblazer 325 / SN: NK12345' : 'e.g., Powermax 65 / SN: 65-1200'} {...field} className="border-0 border-b-2 border-input rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors h-10 placeholder:text-muted-foreground/30 font-medium" />
            </FormControl>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="space-y-1 pt-2">
            <FormLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold font-mono">Fault Description</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full border-0 border-b-2 border-input bg-transparent px-0 py-2 text-sm shadow-none placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-none font-medium" placeholder="Describe the error code or technical failure in detail..." {...field} />
            </FormControl>
            <FormDescription className="text-[10px] text-right font-mono uppercase tracking-tighter">
              Technicians respond within 4 business hours.
            </FormDescription>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-xs font-mono text-destructive uppercase">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-12 text-sm font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300 rounded-sm">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </> : <>
              Submit Repair Ticket <ArrowRight className="ml-2 h-5 w-5" />
            </>}
        </Button>
      </div>
    </Form>;
}